import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The overlap figure: one control, every framework it satisfies, citations visible.
 *
 * This is the site's single visual argument and it is meant to be reused —
 * problem section, the coverage page header, the sales deck. It draws in
 * `currentColor` so it works on the Brand Ink band and on a light ground
 * without a second copy.
 *
 * It rotates through four controls the way the hero rotates scenarios, so a
 * reader who stays sees the claim hold across security, resilience and
 * third-party risk rather than in one cherry-picked case.
 *
 * SHIP RULE: never draw a framework whose citation is not real. Every pairing
 * below exists in production with its source_ref attached (verified
 * 2026-08-30). Re-verify against template_requirements.source_ref before
 * adding, swapping or reordering any of them.
 */
const SCENARIOS = [
  {
    control: 'crypto',
    frameworks: [
      { name: 'ISO 27001', citation: 'A.8.24 Use of cryptography' },
      { name: 'NIS2', citation: 'Article 21(2), point (h)' },
      { name: 'GDPR', citation: 'Article 32 Security of processing' },
    ],
  },
  {
    control: 'access',
    frameworks: [
      { name: 'SOC 2', citation: 'CC6.1 Logical access security' },
      { name: 'ISO 27001', citation: 'A.5.15 Access control' },
      { name: 'DORA', citation: 'Article 9(4)(c)' },
    ],
  },
  {
    control: 'backup',
    frameworks: [
      { name: 'ISO 27001', citation: 'A.8.13 Information backup' },
      { name: 'NIS2', citation: 'Article 21(2), point (c)' },
      { name: 'DORA', citation: 'Article 12(1)(a) backup policies' },
    ],
  },
  {
    control: 'thirdParty',
    frameworks: [
      { name: 'DORA', citation: 'Article 28(1)' },
      { name: 'NIS2', citation: 'Article 21(2), point (d)' },
      { name: 'RBI CTRRA', citation: 'Chapter V, Section Q' },
    ],
  },
] as const;

/**
 * SVG <text> does not wrap. The control box is 236px wide with 216px of usable
 * text width, so labels are split on word boundaries to a character budget.
 * German and French run long, hence two lines rather than truncation.
 */
const wrap = (text: string, maxChars: number, maxLines = 2): string[] => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (next.length <= maxChars || !line) { line = next; continue; }
    lines.push(line);
    line = w;
    if (lines.length === maxLines - 1) break;
  }
  const rest = words.slice(lines.join(' ').split(/\s+/).filter(Boolean).length).join(' ');
  if (lines.length < maxLines && (line || rest)) lines.push(rest || line);
  return lines.slice(0, maxLines);
};

const ROWS = [34, 116, 198];
const HOLD_MS = 5200;
const FADE_MS = 240;

const ControlOverlapFigure = () => {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [lit, setLit] = useState(true);
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let interval: ReturnType<typeof setInterval> | undefined;
    let swap: ReturnType<typeof setTimeout> | undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !interval) {
          interval = setInterval(() => {
            setLit(false);
            swap = setTimeout(() => {
              setIdx((i) => (i + 1) % SCENARIOS.length);
              setLit(true);
            }, FADE_MS);
          }, HOLD_MS);
        } else if (!entry.isIntersecting && interval) {
          clearInterval(interval); interval = undefined;
        }
      },
      { threshold: 0, rootMargin: '0px 0px -15% 0px' },
    );
    io.observe(el);
    return () => { io.disconnect(); if (interval) clearInterval(interval); if (swap) clearTimeout(swap); };
  }, []);

  const s = SCENARIOS[idx];
  const controlName = t(`secondFramework.controls.${s.control}.name`);
  const controlSub = t(`secondFramework.controls.${s.control}.sub`);
  const nameLines = wrap(controlName, 24);
  const subLines = wrap(controlSub, 32);

  return (
    <figure className="m-0">
      <svg
        ref={ref}
        viewBox="0 0 720 268"
        role="img"
        aria-label={`${controlName}: ${s.frameworks.map((f) => `${f.name} ${f.citation}`).join(', ')}`}
        className="block h-auto w-full max-w-full select-none"
      >
        <defs>
          <marker id="cof-arrow" viewBox="0 0 10 8" refX="9" refY="4" markerWidth="9" markerHeight="7" orient="auto">
            <polygon points="0,0 10,4 0,8" fill="hsl(var(--trail-teal))" />
          </marker>
        </defs>

        {/* Frame and connectors are constant; only the labels rotate. */}
        <text x="24" y="78" fontFamily="IBM Plex Mono, monospace" fontSize="11" letterSpacing="1.6" fill="currentColor" opacity="0.55">
          {t('secondFramework.figureOneLabel').toUpperCase()}
        </text>
        <rect x="24" y="88" width="236" height="96" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.85" />
        <path d="M260 136 C 320 136, 350 52, 424 52" fill="none" stroke="hsl(var(--trail-teal))" strokeWidth="1.6" markerEnd="url(#cof-arrow)" />
        <path d="M260 136 L 424 136" fill="none" stroke="hsl(var(--trail-teal))" strokeWidth="1.6" markerEnd="url(#cof-arrow)" />
        <path d="M260 136 C 320 136, 350 216, 424 216" fill="none" stroke="hsl(var(--trail-teal))" strokeWidth="1.6" markerEnd="url(#cof-arrow)" />
        <text x="440" y="24" fontFamily="IBM Plex Mono, monospace" fontSize="11" letterSpacing="1.6" fill="currentColor" opacity="0.55">
          {t('secondFramework.figureManyLabel').toUpperCase()}
        </text>
        {ROWS.map((y) => (
          <rect key={y} x="440" y={y} width="256" height="38" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
        ))}

        <g style={{ opacity: lit ? 1 : 0, transition: `opacity ${FADE_MS}ms ease` }}>
          {nameLines.map((line, i) => (
            <text key={`n${i}`} x="44" y={116 + i * 19} fontSize="15" fontWeight="600" fill="currentColor">{line}</text>
          ))}
          {subLines.map((line, i) => (
            <text key={`s${i}`} x="44" y={116 + nameLines.length * 19 + 4 + i * 16} fontSize="12" fill="currentColor" opacity="0.6">{line}</text>
          ))}
          {s.frameworks.map((f, i) => (
            <g key={f.name + f.citation}>
              <text x="456" y={ROWS[i] + 18} fontSize="14" fontWeight="600" fill="currentColor">{f.name}</text>
              <text x="456" y={ROWS[i] + 32} fontFamily="IBM Plex Mono, monospace" fontSize="11" fill="currentColor" opacity="0.62">{f.citation}</text>
            </g>
          ))}
        </g>

        {/* Position marks, so it reads as a rotation rather than a glitch. */}
        {SCENARIOS.map((_, i) => (
          <rect key={i} x={24 + i * 14} y="206" width="8" height="3"
            fill={i === idx ? 'hsl(var(--trail-teal))' : 'currentColor'}
            opacity={i === idx ? 1 : 0.28}
            style={{ transition: 'opacity 300ms ease, fill 300ms ease' }} />
        ))}
      </svg>
      <figcaption className="mt-4 text-sm opacity-70">{t('secondFramework.figureCaption')}</figcaption>
    </figure>
  );
};

export default ControlOverlapFigure;
